/** @jsxImportSource react */
'use client';

import { useState } from 'react';

import Link from 'next/link';

import { formatPrice, type Plan, PLANS } from '@marketing/lib/pricing';
import { motion } from 'motion/react';

import { track } from './analytics';
import { ArrowRight } from './icons';
import { Eyebrow, Heading, Section } from './section';

// Church-size personalizer. Pastor picks their size; the section highlights the
// right pricing tier with copy that speaks specifically to their context.

type Size = 'plant' | 'growing' | 'large';

/** A plan off canonical PLANS, so this picker can't drift from /pricing. */
function plan(id: Plan['id']): Plan {
  const found = PLANS.find(p => p.id === id);

  if (!found) throw new Error(`size-selector: no plan "${id}"`);

  return found;
}

const annualPrice = (id: Plan['id']) =>
  `${formatPrice(plan(id).priceAnnual)}/yr`;

const STARTER = plan('starter');

const SIZES: { id: Size; label: string; range: string }[] = [
  { id: 'plant', label: 'Church plant', range: 'Under 500' },
  { id: 'growing', label: 'Growing church', range: '500–1,500' },
  { id: 'large', label: 'Large / multi-site', range: '1,500+' },
];

const SIZE_DETAILS: Record<
  Size,
  {
    headline: string;
    body: string;
    tier: { name: string; price: string; href: string; cta: string };
    customerQuote: { quote: string; attribution: string };
  }
> = {
  growing: {
    body: 'Churches your size are where OnArk usually starts paying for itself — the volume of guests through new-member, growth-track, and Dream Team pathways is too much for one staff member to track by hand. Growth includes 500 check-ins/year, with every feature — custom branding, live chat, Planning Center sync — included.',
    customerQuote: {
      attribution: 'Bobby · MyLife Church · 250–500 weekly',
      quote: 'Our serve team doubled in a few months.',
    },
    headline: 'The plan we recommend.',
    tier: {
      cta: 'See Growth details',
      href: '/pricing',
      name: 'Growth',
      price: annualPrice('growth'),
    },
  },
  large: {
    body: 'At your size, Planning Center sync is non-negotiable, and so is check-in volume. Scale includes unlimited check-ins, with every feature — custom branding, live chat, Planning Center sync — included.',
    customerQuote: {
      attribution: 'Bradford · Northwood Church · 1,000–2,500 weekly',
      quote:
        'It integrated with PCO in a way that made next steps easier for our church and our staff.',
    },
    headline: 'Multi-campus, real volume, real automation.',
    tier: {
      cta: 'See Scale details',
      href: '/pricing',
      name: 'Scale',
      price: annualPrice('scale'),
    },
  },
  plant: {
    // Signup pre-selects Growth, so name Starter and tell them to pick it.
    // A bare "you're at $390/year" here would be charged $990 on day 21.
    body: `Most church plants and small churches build and publish their first growth track during a free 21-day trial: real classes, real check-ins, before you pay. A card's on file, $0 today. ${STARTER.name} is ${formatPrice(STARTER.priceAnnual)}/year with ${STARTER.checkInsPerYear} check-ins included. Pick it when you sign up.`,
    customerQuote: {
      attribution: 'Johnny · Discover Church · 100–250 weekly',
      quote: "Once setup was done, it's basically on autopilot.",
    },
    headline: 'Start free for 21 days.',
    tier: {
      cta: 'See Starter details',
      href: '/pricing',
      name: 'Starter',
      price: annualPrice('starter'),
    },
  },
};

export function SizeSelector() {
  const [size, setSize] = useState<Size>('growing');
  const details = SIZE_DETAILS[size];

  return (
    <Section size="md" tone="mist">
      <div className="flex flex-col gap-12">
        {/* Header + selector */}
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.4fr] lg:items-end">
          <div className="flex flex-col gap-5">
            <Eyebrow>Built for your size</Eyebrow>
            <Heading size="lg">Pick your church size.</Heading>
          </div>
          <div
            aria-label="Select church size"
            className="flex flex-wrap gap-3"
            role="radiogroup"
          >
            {SIZES.map(s => {
              const active = s.id === size;

              return (
                <button
                  key={s.id}
                  aria-checked={active}
                  className={[
                    'flex flex-col gap-1 rounded-[14px] border px-5 py-3.5 text-left transition-all',
                    active
                      ? 'border-[color:var(--color-amethyst)] bg-[color:var(--color-amethyst)] text-white shadow-[0_12px_32px_-12px_rgba(123,44,255,0.45)]'
                      : 'border-[color:var(--color-border-soft)] bg-white text-[color:var(--color-ink)] hover:border-[color:var(--color-lilac)]',
                  ].join(' ')}
                  role="radio"
                  type="button"
                  onClick={() => {
                    setSize(s.id);
                    track('size_selector_picked', { size: s.id });
                  }}
                >
                  <span className="text-[15px] font-semibold tracking-[-0.005em]">
                    {s.label}
                  </span>
                  <span
                    className={[
                      'text-[12px]',
                      active
                        ? 'text-white/80'
                        : 'text-[color:var(--color-gray-dark)]',
                    ].join(' ')}
                  >
                    {s.range} weekly
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Personalized content */}
        <motion.div
          key={size}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 gap-8 rounded-[24px] bg-white p-9 ring-1 ring-[color:var(--color-border-soft)] lg:grid-cols-[1.4fr_1fr] lg:p-12"
          initial={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex flex-col gap-5">
            <h3 className="text-[clamp(28px,3.5vw,40px)] font-bold tracking-[-0.02em] [line-height:1.15] text-[color:var(--color-ink)]">
              {details.headline}
            </h3>
            <p className="max-w-[560px] text-[17px] leading-[1.66] text-[color:var(--color-gray-dark)]">
              {details.body}
            </p>
            <div className="flex flex-wrap items-center gap-4 pt-3">
              <Link
                className="group inline-flex items-center gap-2.5 rounded-[12px] bg-[color:var(--color-amethyst)] px-7 py-4 text-[15px] font-semibold text-white shadow-[0_12px_32px_-8px_rgba(123,44,255,0.35)] transition-all hover:-translate-y-0.5"
                href={details.tier.href}
                onClick={() =>
                  track('size_recommendation_clicked', {
                    size,
                    tier: details.tier.name,
                  })
                }
              >
                <span>{details.tier.cta}</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <span className="text-[14px] text-[color:var(--color-gray-dark)]">
                Recommended plan:{' '}
                <span className="font-semibold text-[color:var(--color-ink)]">
                  {details.tier.name} · {details.tier.price}
                </span>
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-4 self-stretch border-t border-[color:var(--color-border-soft)] pt-6 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-[color:var(--color-amethyst)]">
              From a customer your size
            </span>
            <p className="text-[18px] font-medium italic leading-[1.5] tracking-[-0.005em] text-[color:var(--color-ink)]">
              &ldquo;{details.customerQuote.quote}&rdquo;
            </p>
            <span className="text-[13px] text-[color:var(--color-gray-dark)]">
              {details.customerQuote.attribution}
            </span>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}
